<template>
  <div>
    <div class="modal modal-blur fade" :class="{ show: !!user }" :style="{ display: user ? 'block' : 'none' }" tabindex="-1">
      <div class="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ user?.name || user?.email }}</h5>
            <button type="button" class="btn-close" @click="$emit('close')"></button>
          </div>

          <ul class="nav nav-tabs flex-nowrap px-3 pt-2 tab-strip">
            <li v-for="tab of TABS" :key="tab" class="nav-item">
              <button class="nav-link text-nowrap" :class="activeTab === tab ? 'active' : ''" @click="activeTab = tab">
                {{ tab }}
              </button>
            </li>
          </ul>

          <div class="modal-body">
            <div v-if="activeTab === 'Overview'">
              <div class="d-flex align-items-center mb-3">
                <span class="avatar avatar-md me-3" :style="avatarStyle">
                  <template v-if="!user?.has_image">{{ initials }}</template>
                </span>
                <div>
                  <div v-if="user?.name" class="fw-bold">{{ user.name }}</div>
                  <div class="text-secondary">{{ user?.email }}</div>
                </div>
              </div>

              <div v-if="isLocked" class="alert alert-danger">
                Account locked after {{ user.password_fails }} failed sign-ins. It unlocks through the
                email verification flow &mdash; there is no admin unlock action yet.
              </div>

              <dl class="row mb-0">
                <dt class="col-5 col-sm-4 text-secondary">Two-factor</dt>
                <dd class="col-7 col-sm-8">
                  <span class="badge" :class="user?.totp_enabled ? 'bg-green-lt' : 'bg-secondary-lt'">
                    {{ user?.totp_enabled ? 'Enabled' : 'Not enabled' }}
                  </span>
                </dd>
                <dt class="col-5 col-sm-4 text-secondary">Failed sign-ins</dt>
                <dd class="col-7 col-sm-8" :class="isLocked ? 'text-danger' : ''">
                  {{ user?.password_fails }} / {{ LOCK_THRESHOLD }}
                </dd>
                <dt class="col-5 col-sm-4 text-secondary">Created</dt>
                <dd class="col-7 col-sm-8">{{ formatDateTime(user?.created_at) }}</dd>
                <dt class="col-5 col-sm-4 text-secondary">Updated</dt>
                <dd class="col-7 col-sm-8">{{ formatDateTime(user?.updated_at) }}</dd>
              </dl>
            </div>

            <div v-else-if="activeTab === 'Roles'">
              <div v-if="user?.roles?.length" class="mb-3">
                <span v-for="role of user.roles" :key="role.id" class="badge role-badge me-1 mb-1"
                  :class="role.role === 'SUPER_ADMIN' ? 'bg-red-lt' : 'bg-blue-lt'">{{ role.role }}</span>
              </div>
              <p v-else class="text-secondary">
                No roles. This user cannot sign in to anything until at least USER is granted.
              </p>
              <button class="btn" @click="$emit('edit-roles', user)">Edit roles&hellip;</button>
            </div>

            <div v-else-if="activeTab === 'Organizations'">
              <table v-if="user?.organizations?.length" class="table table-sm">
                <thead>
                  <tr><th scope="col">Organization</th><th scope="col">ID</th></tr>
                </thead>
                <tbody>
                  <tr v-for="organization of user.organizations" :key="organization.id">
                    <td>{{ organization.name }}</td>
                    <td class="text-secondary">{{ organization.id }}</td>
                  </tr>
                </tbody>
              </table>
              <p v-else class="text-secondary mb-0">Not a member of any organization.</p>
            </div>

            <div v-else-if="activeTab === 'Security log'">
              <div v-if="logsLoading" class="text-secondary text-center py-4">
                <span class="spinner-border spinner-border-sm me-2"></span>Loading log&hellip;
              </div>
              <div v-else-if="logs.length" class="table-responsive">
                <table class="table table-sm log-table">
                  <thead>
                    <tr>
                      <th scope="col">When</th>
                      <th scope="col">Action</th>
                      <th scope="col">Detail</th>
                      <th scope="col">IP</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="entry of logs" :key="entry.id">
                      <td class="text-nowrap text-secondary">{{ formatDateTime(entry.created_at) }}</td>
                      <td><span class="badge bg-secondary-lt text-nowrap">{{ entry.action }}</span></td>
                      <td>{{ entry.log }}</td>
                      <td class="text-secondary">{{ entry.ip || '—' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p v-else class="text-secondary mb-0">No security log entries.</p>

              <div v-if="logsTotal > logs.length || logsPage > 1" class="d-flex align-items-center gap-2 mt-2">
                <button class="btn btn-sm" :disabled="logsPage === 1" @click="loadLogs(logsPage - 1)">Previous</button>
                <span class="text-secondary small">Page {{ logsPage }} of {{ logsPagesCount }}</span>
                <button class="btn btn-sm" :disabled="logsPage >= logsPagesCount" @click="loadLogs(logsPage + 1)">Next</button>
              </div>
            </div>
          </div>

          <div class="modal-footer">
            <button type="button" class="btn" @click="$emit('close')">Close</button>
          </div>
        </div>
      </div>
    </div>
    <div class="modal-backdrop fade show" v-if="user"></div>
  </div>
</template>

<script setup>
import { computed, getCurrentInstance, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { api } from '@/api'

const props = defineProps({
  user: { type: Object, default: null }
})
defineEmits(['close', 'edit-roles'])

const $vue = getCurrentInstance()
const store = useStore()

const TABS = ['Overview', 'Roles', 'Organizations', 'Security log']
const LOCK_THRESHOLD = 10
const LOGS_PER_PAGE = 25

const activeTab = ref('Overview')
const logs = ref([])
const logsTotal = ref(0)
const logsPage = ref(1)
const logsLoading = ref(false)

const isLocked = computed(() => (props.user?.password_fails || 0) >= LOCK_THRESHOLD)
const logsPagesCount = computed(() => Math.max(1, Math.ceil(logsTotal.value / LOGS_PER_PAGE)))

const initials = computed(() => {
  const source = props.user?.name || props.user?.email || ''
  return source.split(/[\s@._+-]+/).filter(Boolean).slice(0, 2).map(part => part[0].toUpperCase()).join('')
})

const avatarStyle = computed(() => {
  if (!props.user?.has_image) return {}
  return { backgroundImage: 'url(' + api.defaults.baseURL + '/admin/users/' + props.user.id + '/image)' }
})

function formatDateTime (value) {
  if (!value) return '—'
  return new Date(value).toLocaleString()
}

async function loadLogs (page) {
  if (!props.user) return
  logsLoading.value = true
  try {
    const response = await store.dispatch('getUserSecurityLogs', {
      id: props.user.id,
      params: { page, perPage: LOGS_PER_PAGE }
    })
    logs.value = response.Data.items || []
    logsTotal.value = response.Data.total || 0
    logsPage.value = page
  } catch (err) {
    $vue?.proxy.$swal.fire({ icon: 'error', text: err?.response?.data?.Data || 'Failed to load security log' })
  } finally {
    logsLoading.value = false
  }
}

watch(() => props.user, () => {
  activeTab.value = 'Overview'
  logs.value = []
  logsTotal.value = 0
  logsPage.value = 1
})

watch(activeTab, (tab) => {
  if (tab === 'Security log' && !logs.value.length) loadLogs(1)
})
</script>

<style scoped>
.tab-strip {
  overflow-x: auto;
  scrollbar-width: none;
}
.tab-strip::-webkit-scrollbar {
  display: none;
}

@media (max-width: 767.98px) {
  .log-table,
  .log-table tbody,
  .log-table tr,
  .log-table td {
    display: block;
    width: 100%;
  }
  .log-table thead {
    display: none;
  }
  .log-table tr {
    padding: 0.5rem 0;
    border-bottom: 1px solid var(--tblr-border-color);
  }
  .log-table td {
    border: 0;
    padding: 0.0625rem 0;
  }
}
</style>
